import { saveAs } from 'file-saver';

import { ReduxThunkType } from '../../types/ReduxThunkType';
import { Actions as ProjectActions } from './actions';
import { RestErrorResponse } from '../../types/response/RestErrorResponse';
import ProjectService from '../../services/ProjectsService';
import ProjectSettingsService from '../../services/ProjectSettingsService';
import ProjectDocumentsService from '../../services/ProjectDocumentsService';
import { ProjectSortTypes } from '../../enums/ProjectSortTypes';
import { projectListSorter } from '../../utils/projectListSorter';
import { convertBinaryArrayToBlob } from '../../utils/convertBinaryArrayToBlob';
import { ProjectSettings, ProjectStatus, SubcontractorAccount } from './types';
import { spawnSaveSuccessGlobalNotification } from '../global-notifications/asyncActions';
import { ProjectDetailsRequest } from '../../types/services/ProjectDetailsRequest';
import { UserRoleType } from '../user/types';
import { scrollToTop } from '../../../pages/project-settings/project-settings-content/helpers';

export const getProjects =
  (active = false): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getProjects());
      const response = await ProjectService.getProjects(active);
      dispatch(ProjectActions.getProjectsSuccess(response.data.content));
      projectListSorter(response.data.content, 'desc', ProjectSortTypes.ProjectNumber);
    } catch (error) {
      dispatch(ProjectActions.getProjectsError(error as RestErrorResponse));
    }
  };

export const getProjectDetails =
  (id: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getProjectDetails());
      const response = await ProjectSettingsService.getProjectDetails(id);
      dispatch(ProjectActions.getProjectDetailsSuccess(response.data.content));
    } catch (error) {
      dispatch(ProjectActions.getProjectDetailsError(error as RestErrorResponse));
    }
  };

export const saveProjectDetails =
  (projectDetails: ProjectDetailsRequest, projectId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.saveProjectDetails());
      const response = await ProjectSettingsService.saveProjectDetails(projectDetails, projectId);
      dispatch(ProjectActions.saveProjectDetailsSuccess(response.data.content));
      scrollToTop();
      dispatch(spawnSaveSuccessGlobalNotification());
    } catch (error) {
      dispatch(ProjectActions.saveProjectDetailsError(error as RestErrorResponse));
    }
  };

export const getProjectFile =
  (projectId: number, documentId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getProjectFile());
      const file: any = await ProjectDocumentsService.getProjectDocument(projectId, documentId);
      const blob = convertBinaryArrayToBlob(file.data);
      saveAs(blob, file.data.fileDownloadName);
      dispatch(ProjectActions.getProjectFileSuccess());
    } catch (error) {
      dispatch(ProjectActions.getProjectFileError(error as RestErrorResponse));
    }
  };

export const getInternalUserProjects =
  (userId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getInternalUserProjects());
      const response = await ProjectService.getInternalUserProjects(userId);
      dispatch(ProjectActions.getInternalUserProjectsSuccess(response.data.content));
    } catch (error) {
      dispatch(ProjectActions.getInternalUserProjectsError(error as RestErrorResponse));
    }
  };

export const getProjectSettings =
  (projectId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getProjectSettings());
      const response = await ProjectSettingsService.getProjectSettings(projectId);
      dispatch(ProjectActions.getProjectSettingsSuccess(response.data.content));
    } catch (error) {
      dispatch(ProjectActions.getProjectSettingsError(error as RestErrorResponse));
    }
  };

export const changeProjectStatus =
  (projectId: number, status: ProjectStatus): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.changeProjectStatus());
      await ProjectService.changeProjectStatus(projectId, status);
      dispatch(ProjectActions.changeProjectStatusSuccess());
    } catch (error) {
      dispatch(ProjectActions.changeProjectStatusError(error as RestErrorResponse));
    }
  };

export const getProjectContacts =
  (projectId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getProjectContacts());
      const response = await ProjectService.getContactPerson(projectId, '');
      dispatch(ProjectActions.getProjectContactsSuccess(response.data.content.projectDetailsContactPersonSearch));
    } catch (error) {
      dispatch(ProjectActions.getProjectContactsError(error as RestErrorResponse));
    }
  };

export const getExternalUserProjects =
  (userId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getExternalUserProjects());
      const response = await ProjectService.getExternalUserProjects(userId);
      dispatch(ProjectActions.getExternalUserProjectsSuccess(response.data.content));
    } catch (error) {
      dispatch(ProjectActions.getExternalUserProjectsError(error as RestErrorResponse));
    }
  };

export const saveProjectSettings =
  (projectId: number, projectSettings: ProjectSettings): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.saveProjectSettings());
      const response = await ProjectSettingsService.saveProjectSettings(projectId, projectSettings);
      dispatch(ProjectActions.saveProjectSettingsSuccess(response.data.content));
      scrollToTop();
      dispatch(spawnSaveSuccessGlobalNotification());
    } catch (error) {
      dispatch(ProjectActions.saveProjectSettingsError(error as RestErrorResponse));
    }
  };

export const getInternalUsersByProjectId =
  (projectId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getInternalUsersByProjectId());
      const result = await ProjectService.getInternalUsersByProjectId(projectId);
      dispatch(ProjectActions.getInternalUsersByProjectIdSuccess(result.data.content));
    } catch (error) {
      dispatch(ProjectActions.getInternalUsersByProjectIdError(error as RestErrorResponse));
    }
  };

export const setInternalUserRole =
  (projectId: number, userId: number, role: UserRoleType): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.setInternalUserRole());
      await ProjectService.setInternalUserRole(projectId, userId, role);
      dispatch(ProjectActions.setInternalUserRoleSuccess());
    } catch (error) {
      dispatch(ProjectActions.setInternalUserRoleError(error as RestErrorResponse));
    }
  };

export const removeInternalUserRole =
  (projectId: number, userId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.removeInternalUserRole());
      await ProjectService.removeInternalUserRole(projectId, userId);
      dispatch(ProjectActions.removeInternalUserRoleSuccess());
    } catch (error) {
      dispatch(ProjectActions.removeInternalUserRoleError(error as RestErrorResponse));
    }
  };

export const getProjectDocuments =
  (projectId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.getProjectDocuments());
      const response = await ProjectDocumentsService.getProjectDocuments(projectId);
      dispatch(ProjectActions.getProjectDocumentsSuccess(response.data.content));
    } catch (error) {
      dispatch(ProjectActions.getProjectDocumentsError(error as RestErrorResponse));
    }
  };

export const validateSubcontractorAccount =
  (projectId: number, email: string, isAssigned: boolean): ReduxThunkType<Promise<SubcontractorAccount | null | false>> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.validateSubcontractorAccount());
      const response = await ProjectService.validateSubcontractorAccount(projectId, email, isAssigned);
      dispatch(ProjectActions.validateSubcontractorAccountSuccess(response.data.content));
      if (response.data.result.outputMessage === 'SubcontractorAlreadyAssignedToProject') {
        return Promise.resolve(false);
      }
      return Promise.resolve(response.data.content);
    } catch (error) {
      dispatch(ProjectActions.validateSubcontractorAccountError(error as RestErrorResponse));
      return Promise.resolve(false);
    }
  };

export const addSubcontractorToProject =
  (projectId: number, subcontractorId: number, notificationsEmail: string, notificationsContactName: string): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.addSubcontractorToProject());
      await ProjectService.addSubcontractorToProject(projectId, subcontractorId, notificationsEmail, notificationsContactName);
      dispatch(ProjectActions.addSubcontractorToProjectSuccess());
    } catch (error) {
      dispatch(ProjectActions.addSubcontractorToProjectError(error as RestErrorResponse));
    }
  };

export const sendLoginData =
  (projectId: number, subcontractorId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(ProjectActions.sendLoginData());
      await ProjectService.sendLoginData(projectId, subcontractorId);
      dispatch(ProjectActions.sendLoginDataSuccess(projectId, subcontractorId));
    } catch (error) {
      dispatch(ProjectActions.sendLoginDataError(error as RestErrorResponse));
    }
  };
